import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  Search as SearchIcon,
  UserPlus,
  RotateCcw,
  Ban
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";


export default function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [targetFilter, setTargetFilter] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter, targetFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (actionFilter !== 'all') params.append('action_type', actionFilter);
      if (targetFilter !== 'all') params.append('target_type', targetFilter);
      
      const response = await api.get(`/admin/audit-logs?${params}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'viewed':
        return <Eye className="w-4 h-4 text-blue-500" />;
      case 'edited':
        return <Edit className="w-4 h-4 text-yellow-500" />;
      case 'deleted':
      case 'soft_deleted':
      case 'hard_deleted':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'created':
        return <UserPlus className="w-4 h-4 text-green-500" />;
      case 'restored':
        return <RotateCcw className="w-4 h-4 text-purple-500" />;
      case 'suspended':
        return <Ban className="w-4 h-4 text-orange-500" />;
      case 'searched':
        return <SearchIcon className="w-4 h-4 text-slate-500" />;
      default:
        return null;
    }
  };

  const getActionBadge = (action) => {
    const styles = {
      viewed: 'bg-blue-100 text-blue-700',
      edited: 'bg-yellow-100 text-yellow-700',
      deleted: 'bg-red-100 text-red-700',
      soft_deleted: 'bg-red-100 text-red-700',
      hard_deleted: 'bg-red-100 text-red-700',
      created: 'bg-green-100 text-green-700',
      restored: 'bg-purple-100 text-purple-700',
      suspended: 'bg-orange-100 text-orange-700',
      searched: 'bg-slate-100 text-slate-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-bold rounded uppercase inline-flex items-center ${styles[action] || 'bg-slate-100'}`}>
        {getActionIcon(action)}
        <span className="ml-1">{action?.replace('_', ' ')}</span>
      </span>
    );
  };

  const getTargetBadge = (target) => {
    const styles = {
      user: 'bg-blue-50 text-blue-700 border-blue-200',
      team: 'bg-green-50 text-green-700 border-green-200',
      game: 'bg-orange-50 text-orange-700 border-orange-200',
      player: 'bg-purple-50 text-purple-700 border-purple-200',
      dashboard: 'bg-slate-50 text-slate-700 border-slate-200',
      users: 'bg-blue-50 text-blue-700 border-blue-200',
      teams: 'bg-green-50 text-green-700 border-green-200',
      games: 'bg-orange-50 text-orange-700 border-orange-200',
      global: 'bg-slate-50 text-slate-700 border-slate-200'
    };
    return (
      <span className={`px-2 py-1 text-xs font-bold rounded capitalize border ${styles[target] || 'bg-slate-50 border-slate-200'}`}>
        {target}
      </span>
    );
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Audit Logs
        </h1>
        <p className="text-slate-600 mt-1">Track all admin actions for accountability</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border-2 border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[160px] h-10">
              <SelectValue placeholder="Action Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="viewed">Viewed</SelectItem>
              <SelectItem value="edited">Edited</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="deleted">Deleted</SelectItem>
              <SelectItem value="restored">Restored</SelectItem>
              <SelectItem value="searched">Searched</SelectItem>
            </SelectContent>
          </Select>
          <Select value={targetFilter} onValueChange={(v) => { setTargetFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[160px] h-10">
              <SelectValue placeholder="Target Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Targets</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="team">Team</SelectItem>
              <SelectItem value="game">Game</SelectItem>
              <SelectItem value="player">Player</SelectItem>
              <SelectItem value="dashboard">Dashboard</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Admin</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Action</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Target</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-slate-500">Loading...</td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-slate-500">No audit logs found</td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.log_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {new Date(log.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(log.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{log.admin_name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500">{log.admin_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">{getActionBadge(log.action_type)}</td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {getTargetBadge(log.target_type)}
                        <p className="text-xs text-slate-500 font-mono">{log.target_id}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">
                      {log.description}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total} logs
          </p>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * limit >= total}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
